import { useCallback, useMemo } from 'react'
import { api } from '../api/client'
import type { Industry } from '../api/client'
import { useItemQuery } from './useItemQuery'
import type { MultiSelectOption } from '../components/common/MultiSelect'

/**
 * Der Branchenbaum als Auswahlliste.
 *
 * Gebaut wie `useExchangeOptions`, und aus demselben Grund: Abruf, Abbildung
 * auf eine Auswahlliste und Sortierung dreimal geschrieben werden dreimal ein
 * bisschen anders.
 *
 * Der WERT trägt die Norm mit: `GICS:10` und nicht `10`. Ein Code allein sagt
 * nicht, in welche der vier Codespalten er gehört — „10" ist bei GICS der
 * Energiesektor, bei SIC nichts. So greift ein Feld auf alle vier Spalten,
 * ohne dass diese Seite die Zuordnung kennen muss; sie steht im Backend, wo
 * auch die Spalten stehen.
 */
export interface IndustryOptions {
  /** Für die Mehrfachauswahl: Name als Beschriftung, `NORM:CODE` als Wert. */
  options: MultiSelectOption[]
  /** Alle Einträge, falls eine Seite mehr braucht als die Auswahlliste. */
  industries: Industry[]
  /** Ist der Baum da? */
  ready: boolean
}

/** Trennt im Filterwert die Norm vom Code. Muss zum Backend passen. */
export const SCHEME_SEPARATOR = ':'

export function useIndustryOptions(enabled: boolean = true): IndustryOptions {
  // `useCallback` ist hier nicht schmückend: Die Ladefunktion steht in den
  // Abhängigkeiten des Effekts. Eine bei jedem Rendern neu gebildete Funktion
  // löst ihn erneut aus, das Ergebnis setzt den Zustand, das rendert neu — und
  // der Abruf läuft endlos, bis der Server mit 429 abriegelt.
  //
  // `size: 200` ist die Obergrenze des Endpunkts, nicht eine Schätzung. Hier
  // stand 2000 — der Server deckelt bei 200 und antwortete mit 500, der Filter
  // blieb leer, und zu sehen war davon nichts als ein Auswahlfeld ohne
  // Einträge.
  //
  // Elf GICS-Sektoren passen bequem. Kommt SIC dazu, trägt eine Auswahlliste
  // tausend Einträge ohnehin nicht mehr; dann braucht der Filter eine Suche im
  // Backend statt einer vollständigen Liste im Browser.
  const laden = useCallback(
    (signal: AbortSignal) => api.industries({ size: 200 }, signal),
    [])
  const { item } = useItemQuery(laden, enabled)

  const industries = useMemo(() => item?.items ?? [], [item])

  const options = useMemo<MultiSelectOption[]>(
    // Sortiert nach Norm, dann nach Name. Nach dem CODE zu sortieren wäre bei
    // GICS fachlich (10 Energie, 15 Rohstoffe, 20 Industrie — von der Förderung
    // zur Verarbeitung), nur sieht man den Code in der Liste nicht: Sie zeigt
    // Namen, und eine Namensliste in Codereihenfolge wirkt unsortiert.
    () => [...industries]
      .sort((a, b) => a.scheme.localeCompare(b.scheme)
                      || a.name.localeCompare(b.name))
      .map((i) => ({
        value: `${i.scheme}${SCHEME_SEPARATOR}${i.code}`,
        label: i.name,
        // Norm und Code als Zusatz: Die Suche greift auch darauf, wer „10"
        // tippt, findet Energy. Und sobald zwei Normen im Baum stehen, ist es
        // der einzige Unterschied zwischen zwei gleichnamigen Einträgen.
        hint: `${i.scheme} ${i.code}`,
      })),
    [industries])

  return { options, industries, ready: Boolean(item) }
}
